import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  PropsWithChildren,
} from 'react';
import { CheckEmailResponse } from '@modules/auth/hooks/api/useAuth';

interface AuthFlowData {
  email: string;
  checkResult: CheckEmailResponse;
}

interface AuthFlowContextType {
  setAuthFlowData: (data: AuthFlowData) => void;
  consumeAuthFlowData: () => AuthFlowData | null;
}

const AuthFlowContext = createContext<AuthFlowContextType>({
  setAuthFlowData: () => {},
  consumeAuthFlowData: () => null,
});

export const AuthFlowProvider = ({ children }: PropsWithChildren) => {
  const [data, setData] = useState<AuthFlowData | null>(null);

  const setAuthFlowData = useCallback((newData: AuthFlowData) => {
    setData(newData);
  }, []);

  const consumeAuthFlowData = useCallback(() => {
    const current = data;
    if (current) {
      setData(null);
    }
    return current;
  }, [data]);

  const contextValue = useMemo(
    () => ({ setAuthFlowData, consumeAuthFlowData }),
    [setAuthFlowData, consumeAuthFlowData]
  );

  return <AuthFlowContext.Provider value={contextValue}>{children}</AuthFlowContext.Provider>;
};

export const useAuthFlow = () => useContext(AuthFlowContext);

export default AuthFlowContext;
